import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import JxForm from '../JxForms/JxForm';
import JxTitleBar from '../JxTitleBar/JxTitleBar';
import JxIconButton from '../JxButtons/JxIconButton';
import JxAnalogClock from '../JxAnalogClock/JxAnalogClock';

import { TITLE_BAR_ACTIONS } from './JxClockTestTitleBarActions';

import '../JxTheme.css';
import './JxClockTestForm.css';

const TICK_MS = 1000;

/**
 * JxClockTestForm — minimal JxForm-based scaffold to verify the class-inheritance chain.
 * @param {boolean} open
 * @param {string} timeZone - IANA timezone, e.g. 'Europe/Amsterdam'
 * @param {() => void} onClose
 */
export default class JxClockTestForm extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'JxClockTestForm';
        this.schema = {};
        this.state = { ...this.state, now: new Date() };
        this.timezone = this.props.timeZone ?? 'Europe/Amsterdam';
    }

    // Literal, hardcoded default — no window.innerWidth/innerHeight centering math.
    getDefaultGeo = () => ({ top: 300, left: 600, width: 200, height: 300 });

    componentDidMount() {
        super.componentDidMount();
        this._tickId = setInterval(() => this.setState({ now: new Date() }), TICK_MS);
        setTimeout(() => this.bringToTop(), 100);
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        // Update timezone when prop changes — constructor only fires once
        if (this.props.timeZone !== prevProps.timeZone) {
            this.timezone = this.props.timeZone ?? 'Europe/Amsterdam';
            this.forceUpdate();
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        clearInterval(this._tickId);
    }

    /**
     * JxTitleBar actions — pin toggle + existing close action.
     * Pin icon reflects isPinned state, same pattern as CommandBarWidget
     * (togglePin/isPinned/panelPinnedClass come from JxFormBase, no new logic here).
     */
    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
            ...TITLE_BAR_ACTIONS(this.props.onClose),
        ];
    }

    render() {
        if (!this.props.open) return null;

        return createPortal(
            <div
                {...this.panelProps}
                style={this.panelStyle}
                className={`jx-clock-test-window ${this.panelActiveClass} ${this.panelPinnedClass}`}
            >
                <JxTitleBar
                    id={`${this.name}_${this.props.id}_titlebar`}
                    title="Clock"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />

                <span className="jx-clock-test-window-timezone">{this.timezone}</span>

                <div className="jx-clock-test-window-body">
                    <JxAnalogClock id={this.props.id} now={this.state.now} timeZone={this.timezone} showDigital={true} />
                </div>
            </div>,
            document.body,
        );
    }
}
